import {
  computeDocument,
  discountFromPercent,
  formatMoney,
  lineGross,
  mulDivRound,
  type DocLineInput,
  type DocumentTotals,
  type RoundOffMode,
} from '@ims/core'

// Pure cart state for the quick-bill screen (reducer + totals), so it is
// testable without React.

export interface CartLine extends DocLineInput {
  key: string
  unitSymbol: string
  allowsDecimal: boolean
  /** Printed maximum retail price per unit (VAT included), for the "above MRP" warning. */
  mrpPaisa?: number | null
  /** Batch-tracked product: split across batches at checkout unless batchId is chosen. */
  trackBatches?: boolean
  /** Serial-tracked product: needs one serial per unit before the sale completes. */
  trackSerials?: boolean
  serials?: readonly string[] | null
  /** The product's retail and wholesale prices, to switch when the price tier changes. */
  retailPricePaisa?: number
  wholesalePricePaisa?: number | null
  /** Lowest price per unit staff may sell at (same VAT basis as the price). */
  minPricePaisa?: number | null
  /** Price typed by hand, or fixed by a pack or a scale sticker: never re-priced. */
  fixedPrice?: boolean
  /**
   * The line's discount as a percent (basis points, 1000 = 10%), kept while the bill is
   * open so a change of quantity or price recomputes `discountPaisa`. Cart only: the
   * invoice stores the amount.
   */
  discountPercentBp?: number | null
}

/** How the bill discount was entered. Only the amount it comes to is stored. */
export type BillDiscountMode = 'amount' | 'percent' | 'final'

export type PriceTier = 'retail' | 'wholesale'

/** The unit price of a product for a price tier. */
export function tierPrice(
  retailPaisa: number,
  wholesalePaisa: number | null | undefined,
  tier: PriceTier,
): number {
  return tier === 'wholesale' && wholesalePaisa != null ? wholesalePaisa : retailPaisa
}

export interface CartState {
  lines: CartLine[]
  /** The bill discount in rupees (mode `amount`). */
  documentDiscountPaisa: number
  /** Mode `percent`: basis points of the bill after line discounts. */
  billPercentBp?: number | null
  /** Mode `final`: what the customer pays; the discount is worked out. */
  billFinalPaisa?: number | null
  billMode?: BillDiscountMode
  /** The line the last scan or search added to (display only: a brief highlight). */
  lastKey?: string
}

export type CartAction =
  | {
      type: 'add'
      line: Omit<CartLine, 'key' | 'qtyMilli'>
      qtyMilli?: number
      /** Always a new line (pack sales priced with a discount must not merge). */
      separate?: boolean
      /** A scanned serial (IMEI) of one unit. */
      serial?: string
    }
  | { type: 'addSerial'; key: string; serial: string }
  | { type: 'setTier'; tier: PriceTier }
  | { type: 'removeSerial'; key: string; serial: string }
  | { type: 'setQty'; key: string; qtyMilli: number }
  | { type: 'setPrice'; key: string; unitPricePaisa: number }
  | { type: 'setDiscount'; key: string; discountPaisa: number }
  | { type: 'setDiscountPercent'; key: string; percentBp: number }
  | { type: 'setBatch'; key: string; batchId: string | null }
  | { type: 'setDocumentDiscount'; discountPaisa: number }
  | { type: 'setBillPercent'; percentBp: number }
  | { type: 'setBillFinal'; totalPaisa: number | null }
  | { type: 'remove'; key: string }
  /** Puts a removed line back where it was (undo). */
  | { type: 'restore'; line: CartLine; index: number }
  /** Replaces the lines (a restaurant order being checked out); the bill discount stays. */
  | { type: 'load'; lines: Omit<CartLine, 'key'>[] }
  | { type: 'clear' }

export const emptyCart: CartState = { lines: [], documentDiscountPaisa: 0 }

/** The line with its discount worked out again from its percent (a line in rupees is kept). */
function withPercent(line: CartLine): CartLine {
  const bp = line.discountPercentBp
  if (bp == null) return line
  return {
    ...line,
    discountPaisa: discountFromPercent(lineGross(line.qtyMilli, line.unitPricePaisa), bp),
  }
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  const next = reduce(state, action)
  return next.lines === state.lines ? next : { ...next, lines: next.lines.map(withPercent) }
}

function reduce(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'add': {
      if (action.serial) {
        const serial = action.serial
        if (state.lines.some((l) => l.serials?.includes(serial))) return state
        const line = state.lines.find(
          (l) =>
            l.productId === action.line.productId &&
            l.unitPricePaisa === action.line.unitPricePaisa,
        )
        if (line) {
          return {
            ...cartReducer(state, { type: 'addSerial', key: line.key, serial }),
            lastKey: line.key,
          }
        }
        const key = crypto.randomUUID()
        return {
          ...state,
          lines: [...state.lines, { ...action.line, qtyMilli: 1000, serials: [serial], key }],
          lastKey: key,
        }
      }
      const qty = action.qtyMilli ?? 1000
      // Scanning the same product again adds to its line.
      const existing =
        action.line.productId && !action.separate
          ? state.lines.find(
              (l) =>
                l.productId === action.line.productId &&
                l.unitPricePaisa === action.line.unitPricePaisa &&
                (l.discountPaisa === 0 || l.discountPercentBp != null),
            )
          : undefined
      if (existing) {
        return {
          ...state,
          lines: state.lines.map((l) =>
            l.key === existing.key ? { ...l, qtyMilli: l.qtyMilli + qty } : l,
          ),
          lastKey: existing.key,
        }
      }
      const key = crypto.randomUUID()
      return {
        ...state,
        lines: [...state.lines, { ...action.line, qtyMilli: qty, key }],
        lastKey: key,
      }
    }
    case 'addSerial': {
      if (state.lines.some((l) => l.serials?.includes(action.serial))) return state
      return {
        ...state,
        lines: state.lines.map((l) => {
          if (l.key !== action.key) return l
          const serials = [...(l.serials ?? []), action.serial]
          // Fills an empty unit first; otherwise the scan is one more unit.
          return { ...l, serials, qtyMilli: Math.max(l.qtyMilli, serials.length * 1000) }
        }),
      }
    }
    case 'removeSerial':
      return {
        ...state,
        lines: state.lines.map((l) => {
          if (l.key !== action.key) return l
          const serials = (l.serials ?? []).filter((x) => x !== action.serial)
          return { ...l, serials, qtyMilli: Math.max(1000, l.qtyMilli - 1000) }
        }),
      }
    case 'setBatch':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.key === action.key ? { ...l, batchId: action.batchId } : l,
        ),
      }
    case 'setQty':
      if (action.qtyMilli <= 0) return cartReducer(state, { type: 'remove', key: action.key })
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.key === action.key
            ? {
                ...l,
                qtyMilli: action.qtyMilli,
                ...(l.serials
                  ? { serials: l.serials.slice(0, Math.floor(action.qtyMilli / 1000)) }
                  : {}),
              }
            : l,
        ),
      }
    case 'setPrice':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.key === action.key
            ? { ...l, unitPricePaisa: Math.max(0, action.unitPricePaisa), fixedPrice: true }
            : l,
        ),
      }
    case 'setTier':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.fixedPrice || l.retailPricePaisa === undefined
            ? l
            : {
                ...l,
                unitPricePaisa: tierPrice(l.retailPricePaisa, l.wholesalePricePaisa, action.tier),
              },
        ),
      }
    case 'setDiscount':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.key === action.key
            ? { ...l, discountPaisa: Math.max(0, action.discountPaisa), discountPercentBp: null }
            : l,
        ),
      }
    case 'setDiscountPercent':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.key === action.key
            ? { ...l, discountPercentBp: Math.min(10000, Math.max(0, action.percentBp)) }
            : l,
        ),
      }
    case 'setDocumentDiscount':
      return {
        ...state,
        billMode: 'amount',
        documentDiscountPaisa: Math.max(0, action.discountPaisa),
      }
    case 'setBillPercent':
      return { ...state, billMode: 'percent', billPercentBp: Math.max(0, action.percentBp) }
    case 'setBillFinal':
      return { ...state, billMode: 'final', billFinalPaisa: action.totalPaisa }
    case 'remove':
      return { ...state, lines: state.lines.filter((l) => l.key !== action.key) }
    case 'restore': {
      if (state.lines.some((l) => l.key === action.line.key)) return state
      const lines = [...state.lines]
      lines.splice(Math.min(action.index, lines.length), 0, action.line)
      return { ...state, lines }
    }
    case 'load':
      return {
        documentDiscountPaisa: state.documentDiscountPaisa,
        billMode: state.billMode,
        billPercentBp: state.billPercentBp,
        billFinalPaisa: state.billFinalPaisa,
        lines: action.lines.map((l) => ({ ...l, key: crypto.randomUUID() })),
      }
    case 'clear':
      return emptyCart
  }
}

/**
 * The minimum price per unit a line is below (discounts count), or null. The
 * server makes the same check on the saved line (with its share of a bill discount).
 */
export function belowMinPrice(
  line: Pick<CartLine, 'qtyMilli' | 'unitPricePaisa' | 'discountPaisa' | 'minPricePaisa'>,
): number | null {
  const min = line.minPricePaisa
  if (min == null || line.qtyMilli <= 0) return null
  const gross = Math.round((line.qtyMilli * line.unitPricePaisa) / 1000)
  return (gross - (line.discountPaisa ?? 0)) * 1000 < min * line.qtyMilli ? min : null
}

/**
 * A scale sticker with a price: the quantity that price buys (rounded up to
 * whole milli-units) and the discount that brings the line to exactly the price.
 */
export function amountPricing(
  unitPricePaisa: number,
  amountPaisa: number,
): { qtyMilli: number; discountPaisa: number } | null {
  if (unitPricePaisa <= 0 || amountPaisa <= 0) return null
  const qtyMilli = Math.ceil((amountPaisa * 1000) / unitPricePaisa)
  const gross = Math.round((qtyMilli * unitPricePaisa) / 1000)
  return { qtyMilli, discountPaisa: Math.max(0, gross - amountPaisa) }
}

export interface StockShortfall {
  productId: string
  description: string
  /** What the bill sells in total (all its lines of that product). */
  needMilli: number
  /** What the selling location has now (may already be below zero). */
  haveMilli: number
}

/**
 * Products the bill sells more of than the location has. Untracked products
 * and serial products (checked unit by unit) are left out.
 */
export function stockShortfalls(
  lines: readonly Pick<
    CartLine,
    'productId' | 'description' | 'qtyMilli' | 'trackStock' | 'trackSerials'
  >[],
  stock: ReadonlyMap<string, number>,
): StockShortfall[] {
  const need = new Map<string, { description: string; qty: number }>()
  for (const l of lines) {
    if (!l.productId || l.trackStock === false || l.trackSerials) continue
    const entry = need.get(l.productId)
    if (entry) entry.qty += l.qtyMilli
    else need.set(l.productId, { description: l.description, qty: l.qtyMilli })
  }
  const out: StockShortfall[] = []
  for (const [productId, { description, qty }] of need) {
    const have = stock.get(productId) ?? 0
    if (qty > have) out.push({ productId, description, needMilli: qty, haveMilli: have })
  }
  return out
}

/** Serial lines whose serials don't cover the quantity yet ("Phone needs 2 more serials"). */
export function missingSerials(lines: readonly CartLine[]): string | null {
  for (const l of lines) {
    if (!l.trackSerials) continue
    const missing = l.qtyMilli / 1000 - (l.serials?.length ?? 0)
    if (!Number.isInteger(l.qtyMilli / 1000))
      return `${l.description}: serial items are sold in whole units`
    if (missing > 0)
      return `${l.description}: enter ${missing} more serial number${missing > 1 ? 's' : ''}`
  }
  return null
}

export interface CartTotals extends DocumentTotals {
  /** Set when the cart can't be priced (e.g. a discount larger than the line). */
  error: string | null
  /** The lines' own discounts, and the bill discount on top (they add up to `discountPaisa`). */
  lineDiscountPaisa: number
  billDiscountPaisa: number
  /** The bill after line discounts: what a bill percent or a final amount works from. */
  afterLinesPaisa: number
}

/** The bill after each line's own discount. */
export function afterLineDiscounts(lines: readonly CartLine[]): number {
  return lines.reduce(
    (a, l) => a + lineGross(l.qtyMilli, l.unitPricePaisa) - (l.discountPaisa ?? 0),
    0,
  )
}

/**
 * The bill discount in paisa, from however it was entered. A percent is of the bill after
 * line discounts; a final amount gets the smallest discount whose total (before round-off)
 * is at most that amount, so prices that include VAT come out exact. Throws when it can't be
 * met, with words for the person at the counter.
 */
export function billDiscountPaisa(state: CartState): number {
  const mode = state.billMode ?? 'amount'
  if (mode === 'amount') return state.documentDiscountPaisa
  const base = afterLineDiscounts(state.lines)
  if (mode === 'percent') {
    const bp = state.billPercentBp ?? 0
    if (bp > 10000) throw new Error('A discount can be at most 100%.')
    return discountFromPercent(base, bp)
  }
  const target = state.billFinalPaisa
  if (target == null) return 0
  const total = (d: number) =>
    computeDocument(state.lines, { documentDiscountPaisa: d, roundOff: 'none' }).totals.totalPaisa
  if (target < 0) throw new Error('A final amount cannot be below zero.')
  if (total(0) <= target) {
    if (total(0) < target)
      throw new Error(
        `The bill is ${formatMoney(total(0), { symbol: 'Rs.' })}. A final amount can only be lower.`,
      )
    return 0
  }
  let lo = 0
  let hi = base
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (total(mid) <= target) hi = mid
    else lo = mid + 1
  }
  return lo
}

export function cartTotals(state: CartState, roundOff: RoundOffMode = 'nearest_rupee'): CartTotals {
  const zero = {
    subtotalPaisa: 0,
    discountPaisa: 0,
    taxablePaisa: 0,
    taxPaisa: 0,
    roundOffPaisa: 0,
    totalPaisa: 0,
  }
  const parts = { lineDiscountPaisa: 0, billDiscountPaisa: 0, afterLinesPaisa: 0 }
  if (state.lines.length === 0) return { ...zero, ...parts, error: null }
  try {
    const bill = billDiscountPaisa(state)
    const totals = computeDocument(state.lines, { documentDiscountPaisa: bill, roundOff }).totals
    return {
      ...totals,
      lineDiscountPaisa: totals.discountPaisa - bill,
      billDiscountPaisa: bill,
      afterLinesPaisa: afterLineDiscounts(state.lines),
      error: null,
    }
  } catch (error) {
    return {
      ...zero,
      ...parts,
      error: error instanceof Error ? error.message.replace(/^[a-z_]+: /, '') : String(error),
    }
  }
}

/**
 * A pack sold at its own price ("Box of 12" for Rs. 5,200): the line keeps the
 * base unit (so stock falls by 12), with the unit price rounded up and a
 * discount that brings the line to exactly the pack price.
 */
export function packPricing(
  packQtyMilli: number,
  packPricePaisa: number,
): { unitPricePaisa: number; discountPaisa: number } {
  const unitPricePaisa = Math.ceil((packPricePaisa * 1000) / packQtyMilli)
  const gross = mulDivRound(packQtyMilli, unitPricePaisa, 1000)
  return { unitPricePaisa, discountPaisa: Math.max(0, gross - packPricePaisa) }
}

/** The line's price per unit with VAT, when that is above the printed MRP (else null). */
export function aboveMrp(
  line: Pick<CartLine, 'unitPricePaisa' | 'priceIncludesTax' | 'taxRateBp' | 'mrpPaisa'>,
): number | null {
  if (line.mrpPaisa == null) return null
  const withVat = line.priceIncludesTax
    ? line.unitPricePaisa
    : mulDivRound(line.unitPricePaisa, 10_000 + line.taxRateBp, 10_000)
  return withVat > line.mrpPaisa ? withVat : null
}

/** One-tap amounts for "Received": exact, then the next round notes above the total. */
export function quickAmounts(totalPaisa: number): number[] {
  const up = (step: number) => Math.ceil(totalPaisa / step) * step
  const amounts = [totalPaisa, up(10_000), up(50_000), up(100_000)].filter((a) => a > 0)
  return [...new Set(amounts)]
}
